"use client";

import { SocialAccountDto, SocialName, METRICS_EVENTS } from "@cede/types";
import { OnboardingEligibilityMessage } from "../OnboardingEligibilityMessage";
import { ProfileSocialsList } from "@/components/features/profile";
import { ONBOARDING_STEP_TITLES, ONBOARDING_STEP_DESCRIPTIONS, ONBOARDING_MESSAGES } from "@/constants/onboarding";
import { useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useConnectSocialStep } from "@/hooks/onboarding";
import { useSocialAccounts } from "@/hooks/socials/useSocialAccounts";
import { cn } from "@/lib/utils";
import { Users } from "lucide-react";

export const ConnectSocialStep = () => {
  const { isMobile } = useDevice();
  const {
    socialAccounts,
    addSocialAccount,
    removeSocialAccount,
    isAddingSocialAccount,
    isRemovingSocialAccount,
    isLoading,
  } = useSocialAccounts();
  const { isEligibleToTrade } = useConnectSocialStep();
  const { trackEvent } = useMetricsEvents();

  const handleLinkSocial = (socialName: SocialName) => {
    // Track when user selects a social platform during onboarding
    trackEvent(METRICS_EVENTS.ONBOARDING_SOCIAL_PLATFORM_SELECTED, {
      social_platform: socialName,
    });
    addSocialAccount.mutate(socialName);
  };

  const handleRemoveSocial = (socialAccount: SocialAccountDto) => {
    removeSocialAccount.mutate(socialAccount);
  };

  return (
    <div className="flex flex-col w-full h-full gap-4">
      {/* Header */}
      <div className="text-center flex flex-col gap-4">
        <div className="flex items-center justify-center">
          <div className={cn("bg-cede-primary/20 rounded-full flex items-center justify-center", "w-12 h-12")}>
            <Users className={cn("text-cede-primary", "w-6 h-6")} />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <h2 className={cn("font-semibold text-cede-text-primary", isMobile ? "text-lg" : "text-xl")}>
            {ONBOARDING_STEP_TITLES.CONNECT_SOCIAL}
          </h2>
          <p className={cn("text-cede-text-secondary", isMobile ? "text-sm" : "text-base")}>
            {ONBOARDING_STEP_DESCRIPTIONS.CONNECT_SOCIAL}
          </p>
        </div>
      </div>

      {/* Social accounts list - scrollable */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        <ProfileSocialsList
          onLink={handleLinkSocial}
          onRemove={handleRemoveSocial}
          addSocialAccountLoading={isAddingSocialAccount}
          removeSocialAccountLoading={isRemovingSocialAccount}
          socialAccounts={socialAccounts}
          isLoading={isLoading}
          title={null}
          unstyled
        />
      </div>

      {/* Eligibility message */}
      {isEligibleToTrade && <OnboardingEligibilityMessage message={ONBOARDING_MESSAGES.ELIGIBLE_TO_TRADE} />}
    </div>
  );
};
